// ── EXTRACTION RESULT CARD (fp-nofodesk-9) — extracted from App.js ──
import { useState, useMemo } from "react";
import { DOC_TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { useWindowWidth } from "../hooks/useWindowWidth";
import { GatedButton } from "./PlanGate";
import { exportToExcel } from "../utils/exportExcel";
import { generateId, computeGrantId, apiFetch, API } from "../utils/api";
import { apiTrackGrant, groupBySection } from "../utils/grants";

// ── ExtractionTable ───────────────────────────────────────────
// Code-like values (IDs, amounts, dates, URLs) and prose values both render
// in Inter, the one typeface used across the site.
function isCodeLikeValue(label, value) {
  const l = (label || "").toLowerCase();
  const v = String(value || "").trim();
  if (/^https?:\/\//.test(v)) return true;
  if (/(deadline|date|amount|budget|ceiling|floor|funding|award|number|duration|currency|email|phone|reference|identifier)/.test(l)) return true;
  if (/^[$€£₦]?[\d,.]+ ?(million|billion|m|k|usd|eur|gbp)?$/i.test(v)) return true;
  if (/^[A-Z]{2,}[-–—0-9]/.test(v)) return true;
  return false;
}

function ExtractionTable({ data, orgProfile, onAddToPipeline }) {
  const T = useT();
  const isMobile = useWindowWidth() < 768;
  // eslint-disable-next-line no-unused-vars
  const grantId = computeGrantId(data);
  const [addedToPipeline, setAddedToPipeline] = useState(false);
  const [isTracked,        setIsTracked]        = useState(false);
  const [showChecklist,    setShowChecklist]    = useState(false);
  const [checklist,        setChecklist]        = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checkedItems,     setCheckedItems]     = useState(new Set());
  const [showEligibility,  setShowEligibility]  = useState(false);
  const [eligibility,      setEligibility]      = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [showDraft,        setShowDraft]        = useState(false);
  const [draftSection,     setDraftSection]     = useState("Executive Summary");
  const [draftText,        setDraftText]        = useState("");
  const [draftLoading,     setDraftLoading]     = useState(false);
  const [draftCopied,      setDraftCopied]      = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [copiedAll,        setCopiedAll]        = useState(false);

  const groups = useMemo(() => groupBySection(data), [data]);

  const toggleSection = (title) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title); else next.add(title);
      return next;
    });
  };

  const copyAll = () => {
    if (!navigator.clipboard?.writeText) return;
    const lines = [];
    lines.push(data.opportunityIdentification?.callTitle || "Untitled Opportunity");
    lines.push(data.opportunityIdentification?.funderName || "");
    groups.forEach(g => {
      lines.push("", g.title.toUpperCase());
      g.rows.forEach(r => lines.push(`${r.label}: ${r.value}`));
    });
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }).catch(() => {});
  };

  const handleTrack = async () => {
    await apiTrackGrant(data);
    setIsTracked(true);
  };

  const DRAFT_SECTIONS = ["Executive Summary", "Project Narrative", "Budget Justification", "Eligibility Statement", "Abstract", "Organizational Capacity"];

  const handleDraft = async () => {
    setDraftLoading(true); setDraftText("");
    try {
      const res = await apiFetch(`${API}/draft-section`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ grantData: data, orgProfile, section: draftSection }) });
      const json = await res.json();
      setDraftText(json.draft || "Draft generation failed.");
    } catch { setDraftText("Error generating draft. Please try again."); }
    setDraftLoading(false);
  };

  const copyDraft = () => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(draftText).then(() => { setDraftCopied(true); setTimeout(() => setDraftCopied(false), 2000); }).catch(() => {});
  };

  const handleChecklist = async () => {
    if (showChecklist && checklist !== null) { setShowChecklist(false); return; }
    setShowChecklist(true);
    if (checklist !== null) return;
    setChecklistLoading(true);
    try {
      const res = await apiFetch(`${API}/generate-checklist`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ grantData: data }) });
      setChecklist((await res.json()).checklist || []);
    } catch { setChecklist([]); }
    setChecklistLoading(false);
  };

  const handleEligibility = async () => {
    if (!orgProfile) return;
    if (showEligibility && eligibility !== null) { setShowEligibility(false); return; }
    setShowEligibility(true);
    if (eligibility !== null) return;
    setEligibilityLoading(true);
    try {
      const res = await apiFetch(`${API}/check-eligibility`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ orgProfile, grantData: data }) });
      setEligibility((await res.json()).assessment);
    } catch { setEligibility({ eligible: "unclear", summary: "Assessment failed." }); }
    setEligibilityLoading(false);
  };

  const toggleCheckItem = (itemId) => {
    const updated = new Set(checkedItems);
    if (updated.has(itemId)) updated.delete(itemId); else updated.add(itemId);
    setCheckedItems(updated);
  };

  const primaryType = data._classification?.primary || "Grant";
  const confidence  = data._confidence || "Low";
  const conflicts   = Array.isArray(data._conflicts) ? data._conflicts : [];

  // Light document surface — the signature element (design brief §5)
  const D = DOC_TOKENS;
  const confColor = confidence === "High" ? D.success : confidence === "Medium" ? D.warning : D.danger;

  const monoChip = {
    fontSize: 10, padding: "3px 9px", borderRadius: 5, fontFamily: FONT_MONO,
    letterSpacing: "0.05em", textTransform: "uppercase",
    background: "rgba(91,79,233,0.07)", color: D.textSub, border: `1px solid ${D.border}`,
    whiteSpace: "nowrap",
  };
  const btnDoc = {
    padding: "6px 13px", borderRadius: 7, cursor: "pointer", fontSize: 12,
    fontFamily: FONT_UI, fontWeight: 500, background: D.surface, color: D.textSub,
    border: `1px solid ${D.border}`, transition: "all 0.15s", whiteSpace: "nowrap",
  };
  const btnDocOn = (color) => ({
    ...btnDoc, color, borderColor: `${color}55`, background: `${color}0F`,
  });

  let clTotal = 0, clDone = 0;
  if (checklist) checklist.forEach((cat) => cat.items?.forEach((item) => { clTotal++; if (checkedItems.has(item.id)) clDone++; }));

  const eligBadge = eligibility ? ({
    yes:   { bg: "rgba(14,122,95,0.08)",  border: "rgba(14,122,95,0.35)",  text: D.success, label: "Eligible" },
    no:    { bg: "rgba(180,35,24,0.07)",  border: "rgba(180,35,24,0.3)",   text: D.danger,  label: "Not Eligible" },
    maybe: { bg: "rgba(154,103,0,0.08)",  border: "rgba(154,103,0,0.3)",   text: D.warning, label: "Possibly Eligible" },
  }[eligibility.eligible] || { bg: D.bg, border: D.border, text: D.textMuted, label: "Unclear" }) : null;

  return (
    <div style={{ fontFamily: FONT_H, width: "100%", maxWidth: "100%", boxSizing: "border-box", background: D.bg, color: D.text, borderRadius: 14, overflow: "hidden", animation: "fadeIn 0.35s ease" }}>

      {/* A call that contradicts itself is the most important thing on the card. */}
      {conflicts.length > 0 && (
        <div style={{ background: "rgba(180,35,24,0.07)", borderBottom: "1px solid rgba(180,35,24,0.3)", padding: isMobile ? "12px 16px" : "12px 24px", fontFamily: FONT_UI, fontSize: 12.5, color: D.danger, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ This call contradicts itself on {conflicts.length} point{conflicts.length > 1 ? "s" : ""} — confirm with the funder before relying on either value</div>
          {conflicts.map((c, i) => (
            <div key={i}>
              <b>{c.field}:</b> {c.values.map((v, j) => (
                <span key={j}>{j > 0 ? " vs " : ""}"{v.value}"{v.locator ? <span style={{ opacity: 0.7 }}> ({v.locator})</span> : null}</span>
              ))}
            </div>
          ))}
        </div>
      )}
      {data._truncationWarning && (
        <div style={{ background: "rgba(154,103,0,0.08)", borderBottom: "1px solid rgba(154,103,0,0.3)", padding: isMobile ? "10px 16px" : "10px 24px", fontFamily: FONT_UI, fontSize: 12.5, color: D.warning }}>
          {data._truncationWarning}
        </div>
      )}

      {/* Header — light document surface, actions pinned top-right */}
      <div style={{ background: D.surface, borderBottom: `1px solid ${D.border}`, padding: isMobile ? "14px 16px" : "18px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: D.textMuted, fontFamily: FONT_MONO, marginBottom: 6 }}>Grant Extraction Result</div>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 600, lineHeight: 1.3, fontFamily: FONT_H, color: D.text }}>{data.opportunityIdentification?.callTitle || "Untitled Opportunity"}</div>
            <div style={{ fontSize: 12, color: D.textSub, marginTop: 5, fontFamily: FONT_MONO }}>{data.opportunityIdentification?.funderName || "Unknown Funder"}{data.opportunityIdentification?.opportunityId ? ` · ${data.opportunityIdentification.opportunityId}` : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={copyAll} title="Copy extraction to clipboard" style={copiedAll ? btnDocOn(D.success) : btnDoc}>
              {copiedAll ? "✓ Copied" : "Copy"}
            </button>
            <GatedButton feature="excelExport" T={T}>
              <button onClick={() => exportToExcel(data)} style={{ ...btnDoc, background: D.accent, color: "#fff", border: `1px solid ${D.accent}`, fontWeight: 600 }}>
                ⬇ Excel
              </button>
            </GatedButton>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14, alignItems: "center" }}>
          <span style={monoChip}>{primaryType}</span>
          <span style={{ ...monoChip, color: confColor, borderColor: `${confColor}44`, background: `${confColor}0D` }}>{confidence} confidence</span>
          <span style={{ flex: 1 }} />
          <button onClick={handleTrack} disabled={isTracked} style={isTracked ? btnDocOn(D.success) : btnDoc}>
            {isTracked ? "✓ Tracked" : "Track Deadline"}
          </button>
          <button onClick={handleChecklist} style={showChecklist ? btnDocOn(D.accent) : btnDoc}>
            {checklist && clTotal > 0 ? `Checklist (${clDone}/${clTotal})` : "Checklist"}
          </button>
          {orgProfile && (
            <GatedButton feature="eligibility" T={T}>
              <button onClick={handleEligibility} style={showEligibility ? btnDocOn(D.success) : btnDoc}>
                Check Eligibility
              </button>
            </GatedButton>
          )}
          {onAddToPipeline && (
            <button
              disabled={addedToPipeline}
              onClick={() => {
                onAddToPipeline({
                  id: generateId(),
                  grantName: data.opportunityIdentification?.callTitle || "",
                  funder:    data.opportunityIdentification?.funderName || "",
                  amount:    data.financialInformation?.totalFunding || data.financialInformation?.estimatedFundingRange || "",
                  dateSubmitted: "",
                  status: "Ongoing Application",
                  notes: [data.opportunityIdentification?.callReference, data.opportunityIdentification?.opportunityId].filter(Boolean).join(" · "),
                });
                setAddedToPipeline(true);
              }}
              style={addedToPipeline ? btnDocOn(D.success) : btnDoc}>
              {addedToPipeline ? "✓ Added to Pipeline" : "Add to Pipeline"}
            </button>
          )}
          <button onClick={() => setShowDraft(d => !d)} style={showDraft ? btnDocOn(D.accent) : btnDoc}>
            Draft Section
          </button>
        </div>
      </div>

      {data.complianceAndRisks?.sourceConflicts?.length > 0 && (
        <div style={{ background: "rgba(180,35,24,0.06)", borderLeft: `3px solid ${D.danger}`, padding: "10px 24px", fontSize: 13, color: D.danger, fontFamily: FONT_UI }}>
          <strong>Source conflicts:</strong> {data.complianceAndRisks.sourceConflicts.join(" | ")}
        </div>
      )}

      {groups.map((group, gi) => {
        const isCollapsed = collapsedSections.has(group.title);
        return (
          <div key={gi} style={{ borderTop: gi === 0 ? "none" : `1px solid ${D.borderDim}` }}>
            <button
              onClick={() => toggleSection(group.title)}
              aria-expanded={!isCollapsed}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, background: D.bg, border: "none", padding: isMobile ? "10px 16px" : "10px 24px", cursor: "pointer", textAlign: "left", minHeight: 40 }}>
              <span aria-hidden="true" style={{ fontSize: 9, color: D.textMuted, width: 10, flexShrink: 0 }}>{isCollapsed ? "▶" : "▼"}</span>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.13em", textTransform: "uppercase", color: D.textSub, fontFamily: FONT_MONO }}>{group.title}</span>
              <span style={{ fontSize: 10, color: D.textMuted, fontFamily: FONT_MONO, marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>{group.rows.length} field{group.rows.length === 1 ? "" : "s"}</span>
            </button>
            {!isCollapsed && (
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", background: D.surface }}>
                <colgroup><col style={{ width: isMobile ? "40%" : "32%" }} /><col style={{ width: isMobile ? "60%" : "68%" }} /></colgroup>
                <tbody>
                  {group.rows.map((row, ri) => {
                    const mono = isCodeLikeValue(row.label, row.value);
                    return (
                      <tr key={ri}>
                        <td style={{ padding: isMobile ? "10px 12px 10px 16px" : "11px 14px 11px 24px", fontSize: 12, fontWeight: 600, color: D.textSub, fontFamily: FONT_UI, verticalAlign: "top", wordBreak: "break-word", borderTop: `1px solid ${D.borderDim}`, lineHeight: 1.5 }}>
                          {row.label.includes("›")
                            ? <><span style={{ color: D.textMuted, fontWeight: 400, fontSize: 11 }}>{row.label.split("›").slice(0, -1).join("›")} › </span><span>{row.label.split("›").pop().trim()}</span></>
                            : row.label}
                        </td>
                        <td style={{ padding: isMobile ? "10px 16px 10px 12px" : "11px 24px 11px 14px", fontSize: mono ? 13 : 15, color: D.text, fontFamily: mono ? FONT_MONO : FONT_H, verticalAlign: "top", wordBreak: "break-word", whiteSpace: "pre-wrap", borderTop: `1px solid ${D.borderDim}`, lineHeight: 1.7, fontVariantNumeric: "tabular-nums" }}>
                          {String(row.value).startsWith("http")
                            ? <a href={row.value} target="_blank" rel="noreferrer" style={{ color: D.accent, wordBreak: "break-all", fontFamily: FONT_MONO, fontSize: 13 }}>{row.value}</a>
                            : row.value}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {/* ── AI DRAFT PANEL ──────────────────────────────── */}
      {showDraft && (
        <div style={{ background: D.bg, borderTop: `1px solid ${D.border}`, padding: isMobile ? "16px" : "20px 24px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.13em", textTransform: "uppercase", color: D.textSub, fontFamily: FONT_MONO, marginBottom: 14 }}>AI Grant Section Drafter</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <select value={draftSection} onChange={e => setDraftSection(e.target.value)}
              style={{ padding: "7px 12px", background: D.surface, border: `1px solid ${D.border}`, color: D.text, borderRadius: 7, fontSize: 13, fontFamily: FONT_UI, cursor: "pointer", colorScheme: "light" }}>
              {DRAFT_SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={handleDraft} disabled={draftLoading} style={{ padding: "7px 18px", background: draftLoading ? D.border : D.accent, color: draftLoading ? D.textMuted : "#fff", border: "none", borderRadius: 7, cursor: draftLoading ? "not-allowed" : "pointer", fontSize: 13, fontFamily: FONT_UI, fontWeight: 600 }}>
              {draftLoading ? "Generating…" : "Generate Draft"}
            </button>
            {!orgProfile && <span style={{ fontSize: 11, color: D.warning, fontFamily: FONT_UI }}>Set your org profile in Settings for a tailored draft</span>}
          </div>
          {draftText && (
            <div style={{ position: "relative" }}>
              <textarea readOnly value={draftText} rows={10}
                style={{ width: "100%", background: D.surface, border: `1px solid ${D.border}`, color: D.text, borderRadius: 8, padding: "14px 16px", fontSize: 14, fontFamily: FONT_H, lineHeight: 1.7, resize: "vertical", boxSizing: "border-box", outline: "none", colorScheme: "light" }} />
              <button onClick={copyDraft} style={{ position: "absolute", top: 8, right: 8, ...(draftCopied ? btnDocOn(D.success) : btnDoc), fontSize: 11, padding: "4px 10px" }}>
                {draftCopied ? "✓ Copied" : "Copy"}
              </button>
            </div>
          )}
        </div>
      )}

      {data._parseError && (
        <div style={{ background: "rgba(154,103,0,0.06)", borderTop: `1px solid rgba(154,103,0,0.25)`, padding: "10px 24px", fontSize: 12, color: D.warning, fontFamily: FONT_UI }}>
          Partial extraction — the AI response could not be fully structured. Re-extract or verify fields against the source.
        </div>
      )}

      {/* ── CHECKLIST PANEL ──────────────────────────────── */}
      {showChecklist && (
        <div style={{ background: D.bg, borderTop: `1px solid ${D.border}`, padding: isMobile ? "16px" : "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.13em", textTransform: "uppercase", color: D.textSub, fontFamily: FONT_MONO }}>
              Application Checklist
              {checklist && clTotal > 0 && <span style={{ fontSize: 11, color: D.textMuted, fontWeight: 400, marginLeft: 8, textTransform: "none", letterSpacing: 0, fontVariantNumeric: "tabular-nums" }}>{clDone}/{clTotal} completed</span>}
            </div>
          </div>

          {checklistLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ height: 38, borderRadius: 7, background: D.borderDim, animation: "pulse 1.5s ease-in-out infinite", animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          ) : checklist && checklist.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {checklist.map((cat, ci) => (
                <div key={ci}>
                  <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: D.textMuted, marginBottom: 8, fontFamily: FONT_MONO }}>{cat.category}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {(cat.items || []).map((item) => {
                      const checked = checkedItems.has(item.id);
                      const pColor = { high: D.danger, medium: D.warning, low: D.textMuted }[item.priority] || D.textMuted;
                      return (
                        <div key={item.id} onClick={() => toggleCheckItem(item.id)} role="checkbox" aria-checked={checked} tabIndex={0} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCheckItem(item.id); } }} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 12px", minHeight: 40, boxSizing: "border-box", background: checked ? "rgba(14,122,95,0.06)" : D.surface, border: `1px solid ${checked ? "rgba(14,122,95,0.3)" : D.border}`, borderRadius: 7, cursor: "pointer", opacity: checked ? 0.85 : 1, transition: "all 0.15s" }}>
                          <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1, background: checked ? D.success : "transparent", border: `2px solid ${checked ? D.success : D.textMuted}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {checked && <span style={{ color: "#fff", fontSize: 10 }}>✓</span>}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontFamily: FONT_UI, lineHeight: 1.4, color: checked ? D.success : D.text, textDecoration: checked ? "line-through" : "none" }}>{item.task}</div>
                            {item.notes && <div style={{ fontSize: 11, color: D.textMuted, marginTop: 3, fontFamily: FONT_UI }}>{item.notes}</div>}
                          </div>
                          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: `${pColor}15`, color: pColor, fontFamily: FONT_MONO, flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.priority}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {clTotal > 0 && (
                <div>
                  <div style={{ height: 3, background: D.border, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(clDone / clTotal) * 100}%`, background: D.accent, transition: "width 0.3s ease" }} />
                  </div>
                  <div style={{ fontSize: 10, color: D.textMuted, marginTop: 4, textAlign: "right", fontFamily: FONT_MONO, fontVariantNumeric: "tabular-nums" }}>{Math.round((clDone / clTotal) * 100)}% complete</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: D.textMuted, fontSize: 12, textAlign: "center", padding: "20px 0", fontFamily: FONT_UI }}>
              The checklist could not be generated for this grant. <button onClick={() => { setChecklist(null); handleChecklist(); }} style={{ background: "none", border: "none", color: D.accent, cursor: "pointer", fontSize: 12, textDecoration: "underline", fontFamily: FONT_UI, padding: 0 }}>Try again</button>
            </div>
          )}
        </div>
      )}

      {/* ── ELIGIBILITY PANEL ────────────────────────────── */}
      {showEligibility && (
        <div style={{ background: D.bg, borderTop: `1px solid ${D.border}`, padding: isMobile ? "16px" : "20px 24px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.13em", textTransform: "uppercase", color: D.textSub, fontFamily: FONT_MONO, marginBottom: 16 }}>
            Eligibility Assessment — {orgProfile?.name || "Your Organisation"}
          </div>
          {eligibilityLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}>
              {[0, 1].map(i => (
                <div key={i} style={{ height: 48, borderRadius: 7, background: D.borderDim, animation: "pulse 1.5s ease-in-out infinite", animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          ) : eligibility && eligBadge ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ padding: "12px 16px", borderRadius: 8, background: eligBadge.bg, border: `1px solid ${eligBadge.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: eligBadge.text, fontFamily: FONT_UI }}>{eligBadge.label}</div>
                  <div style={{ fontSize: 12, color: D.textSub, marginTop: 4, fontFamily: FONT_UI, lineHeight: 1.5 }}>{eligibility.summary}</div>
                </div>
                {eligibility.score != null && <div style={{ fontSize: 22, fontWeight: 700, color: eligBadge.text, fontFamily: FONT_MONO, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{eligibility.score}%</div>}
              </div>

              {eligibility.criteria?.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: D.textMuted, marginBottom: 8, fontFamily: FONT_MONO }}>Criteria</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {eligibility.criteria.map((c, i) => {
                      const icon  = { pass: "✓", fail: "✗", unclear: "?" }[c.status] || "?";
                      const color = { pass: D.success, fail: D.danger, unclear: D.warning }[c.status] || D.textMuted;
                      return (
                        <div key={i} style={{ display: "flex", gap: 10, padding: "8px 12px", background: D.surface, border: `1px solid ${D.borderDim}`, borderRadius: 6, alignItems: "flex-start" }}>
                          <span style={{ color, fontWeight: 700, fontSize: 13, flexShrink: 0, fontFamily: FONT_MONO }}>{icon}</span>
                          <div>
                            <div style={{ fontSize: 12, color: D.text, fontFamily: FONT_UI }}>{c.criterion}</div>
                            {c.notes && <div style={{ fontSize: 11, color: D.textMuted, marginTop: 2, fontFamily: FONT_UI }}>{c.notes}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {eligibility.gaps?.length > 0 && (
                <div style={{ padding: "10px 14px", background: "rgba(180,35,24,0.05)", borderRadius: 8, border: "1px solid rgba(180,35,24,0.2)" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: D.danger, marginBottom: 6, fontFamily: FONT_MONO }}>Gaps to Address</div>
                  {eligibility.gaps.map((g, i) => <div key={i} style={{ fontSize: 12, color: D.textSub, marginBottom: 3, fontFamily: FONT_UI, lineHeight: 1.5 }}>• {g}</div>)}
                </div>
              )}

              {eligibility.recommendations?.length > 0 && (
                <div style={{ padding: "10px 14px", background: "rgba(14,122,95,0.05)", borderRadius: 8, border: "1px solid rgba(14,122,95,0.2)" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: D.success, marginBottom: 6, fontFamily: FONT_MONO }}>Recommendations</div>
                  {eligibility.recommendations.map((r, i) => <div key={i} style={{ fontSize: 12, color: D.textSub, marginBottom: 3, fontFamily: FONT_UI, lineHeight: 1.5 }}>• {r}</div>)}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ExtractionTable;
